"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isStatusTrackingPredicateConfigured = isStatusTrackingPredicateConfigured;
exports.isStatusTrackingRowOpen = isStatusTrackingRowOpen;
exports.statusTrackingCellState = statusTrackingCellState;
exports.filterRowsByStatusTracking = filterRowsByStatusTracking;
exports.statusTrackingRowStates = statusTrackingRowStates;
exports.statusTrackingOptionLookup = statusTrackingOptionLookup;
function lookupOptionLabel(value, options) {
    if (!options) {
        return value;
    }
    if (options instanceof Map) {
        return options.get(value) ?? value;
    }
    return options[value] ?? value;
}
function valueToComparableStrings(value, options) {
    if (value === null || value === undefined) {
        return [''];
    }
    if (Array.isArray(value)) {
        return value.flatMap(item => valueToComparableStrings(item, options));
    }
    if (typeof value === 'string') {
        return [lookupOptionLabel(value, options)];
    }
    if (typeof value === 'object') {
        const record = value;
        if (typeof record.value === 'string') {
            return [record.value];
        }
        if (typeof record.id === 'string') {
            return [lookupOptionLabel(record.id, options)];
        }
    }
    return [String(value)];
}
function normalizeComparable(value) {
    return String(value ?? '')
        .trim()
        .toLowerCase();
}
function isStatusTrackingPredicateConfigured(predicate) {
    return (predicate?.enabled === true &&
        predicate.mode !== 'delete_row' &&
        typeof predicate.columnId === 'string' &&
        predicate.columnId.trim().length > 0 &&
        predicate.successValue !== null &&
        predicate.successValue !== undefined &&
        String(predicate.successValue).trim().length > 0);
}
/**
 * Whether a run still takes the row: its status cell holds neither the
 * success nor the error value. Every row is open without status tracking.
 */
function isStatusTrackingRowOpen(predicate, cellValue, options) {
    if (!predicate?.enabled) {
        return true;
    }
    if (!isStatusTrackingPredicateConfigured(predicate)) {
        throw new Error('Status tracking is enabled but columnId or successValue is missing');
    }
    return statusTrackingCellState(predicate, cellValue, options) === 'open';
}
/**
 * What the status cell of a row says: the success value (done), the error
 * value (error) or anything else, an empty cell included (open). Select
 * options compare by their label.
 */
function statusTrackingCellState(predicate, cellValue, options) {
    const values = valueToComparableStrings(cellValue, options).map(normalizeComparable);
    if (values.includes(normalizeComparable(predicate.successValue))) {
        return 'done';
    }
    const errorValue = normalizeComparable(predicate.errorValue);
    if (errorValue && values.includes(errorValue))
        return 'error';
    return 'open';
}
/**
 * Rows status tracking still has to process: rows whose status column holds
 * the success or the error value are not taken again. Select options compare
 * by their label.
 */
function filterRowsByStatusTracking(rows, columns, statusTracking) {
    if (!statusTracking?.enabled) {
        return rows;
    }
    if (!isStatusTrackingPredicateConfigured(statusTracking)) {
        throw new Error('Status tracking is enabled but columnId or successValue is missing');
    }
    const optionLookup = statusTrackingOptionLookup(columns, statusTracking.columnId);
    return rows.filter(row => isStatusTrackingRowOpen(statusTracking, row.cells[statusTracking.columnId]?.value, optionLookup));
}
/**
 * What the status cell of each row says (done, error or open), for a run
 * that holds the rows: done and failed rows get their state without a run.
 */
function statusTrackingRowStates(rows, columns, statusTracking) {
    if (!isStatusTrackingPredicateConfigured(statusTracking)) {
        throw new Error('Status tracking is enabled but columnId or successValue is missing');
    }
    const optionLookup = statusTrackingOptionLookup(columns, statusTracking.columnId);
    return new Map(rows.map(row => [
        row.id,
        statusTrackingCellState(statusTracking, row.cells[statusTracking.columnId]?.value, optionLookup),
    ]));
}
/** Option id to label of a select or multi-select status column. */
function statusTrackingOptionLookup(columns, columnId) {
    const column = columns.find(item => item.id === columnId);
    if (column?.type !== 'select' && column?.type !== 'multi-select') {
        return undefined;
    }
    const options = column.data?.options;
    if (!Array.isArray(options)) {
        return undefined;
    }
    const lookup = new Map();
    for (const option of options) {
        if (option &&
            typeof option === 'object' &&
            !Array.isArray(option) &&
            'id' in option &&
            'value' in option) {
            const { id, value } = option;
            if (typeof id === 'string' && typeof value === 'string') {
                lookup.set(id, value);
            }
        }
    }
    return lookup.size > 0 ? lookup : undefined;
}
